import { api } from '@shared/api';
import { mapSenseContexts } from '@shared/lib';
import type { SenseContext } from '@shared/types';
import type {
  NotificationCategory,
  NotificationDto,
  NotificationEventType,
  NotificationReadFilter,
  NotificationTone,
  NotificationsPage,
} from './types';

interface BackendNotification {
  id: string;
  category: string;
  type: string;
  payload: Record<string, unknown> | null;
  is_read: boolean;
  created_at: string;
}

interface BackendPage {
  items: BackendNotification[];
  next_cursor: string | null;
}

interface UnreadCountResponse {
  count: number;
}

export interface NotificationsQueryArgs {
  filter?: NotificationReadFilter;
  cursor?: string | null;
  limit?: number;
}

export async function fetchNotificationsPage(
  args: NotificationsQueryArgs = {},
): Promise<NotificationsPage> {
  const params: Record<string, string | number> = {
    filter: args.filter ?? 'all',
    limit: args.limit ?? 20,
  };
  if (args.cursor) params.cursor = args.cursor;

  const { data } = await api.get<BackendPage>('/notification/', { params });
  return {
    items: data.items
      .map(buildNotificationFromBackend)
      .filter((n): n is NotificationDto => n !== null),
    nextCursor: data.next_cursor ?? null,
  };
}

export async function fetchUnreadCount(): Promise<number> {
  const { data } = await api.get<UnreadCountResponse>('/notification/unread-count');
  return data.count;
}

export async function markNotificationRead(id: string): Promise<void> {
  await api.patch(`/notification/${id}/read`);
}

/** «Отметить все прочитанными»; возвращает, сколько было отмечено. */
export async function markAllNotificationsRead(): Promise<number> {
  const { data } = await api.post<{ count: number }>('/notification/read-all');
  return data.count;
}

/** «Скрыть все» на вкладке списка; возвращает, сколько скрыто. */
export async function hideAllNotifications(filter: NotificationReadFilter): Promise<number> {
  const { data } = await api.post<{ count: number }>('/notification/hide-all', undefined, { params: { filter } });
  return data.count;
}

export async function hideNotification(id: string): Promise<void> {
  await api.delete(`/notification/${id}`);
}

const NOTIFIABLE_TONE: Partial<Record<NotificationEventType, NotificationTone>> = {
  unExpectedError: 'error',
  expectedWord: 'warning',
  succeeded: 'success',
  selectionFailed: 'warning',
};

/** Выбранная строка подсказок, которая не стала карточкой. */
interface FailedPick {
  pos: string | null;
  translation: string | null;
}

const POS_RU: Record<string, string> = {
  noun: 'существительное',
  verb: 'глагол',
  adjective: 'прилагательное',
  adverb: 'наречие',
  preposition: 'предлог',
  pronoun: 'местоимение',
  conjunction: 'союз',
  interjection: 'междометие',
  numeral: 'числительное',
  particle: 'частица',
  determiner: 'определитель',
  phrase: 'фраза',
};

function readFailedPicks(value: unknown): FailedPick[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((p): p is Record<string, unknown> => p != null && typeof p === 'object')
    .map((p) => ({
      pos: typeof p.pos === 'string' ? p.pos : null,
      translation: typeof p.translation === 'string' ? p.translation : null,
    }));
}

function describeFailedPicks(picks: FailedPick[]): string {
  return picks
    .map((p) => {
      const pos = p.pos ? (POS_RU[p.pos.toLowerCase()] ?? p.pos) : null;
      return [pos, p.translation ? `«${p.translation}»` : null].filter(Boolean).join(' ');
    })
    .filter(Boolean)
    .join(', ');
}

export interface SseTranslation {
  id: string;
  text: string;
  gender: string | null;
}

export interface SseSense {
  id: string;
  translations: SseTranslation[];
  enSynonyms: string[];
  exampleEn: string | null;
  hint: string | null;
  selectionOptions: string[] | null;
  isRare: boolean;
  contexts: SenseContext[];
}

export interface SseMeaning {
  id: string;
  pos: string | null;
  senses: SseSense[];
}

export interface SseCardPayloadFull {
  id: string;
  frontName: string;
  primaryTranslation: string | null;
  transcription: string | null;
  cefr: string | null;
  meanings: SseMeaning[];
  isNeedErrorAction: boolean;
  errorInfo: string | string[] | null;
  expectWord: string[] | null;
  status: string;
}

interface BackendSseSenseShape {
  id?: string;
  translations?: Array<{ id?: string; text: string; gender?: string | null }> | null;
  en_synonyms?: string[] | null;
  enSynonyms?: string[] | null;
  example_en?: string | null;
  exampleEn?: string | null;
  simple_example_en?: string | null;
  simpleExampleEn?: string | null;
  simple_example_ru?: string | null;
  simpleExampleRu?: string | null;
  hint?: string | null;
  selection_options?: string[] | null;
  selectionOptions?: string[] | null;
  is_rare?: boolean | null;
  isRare?: boolean | null;
  contexts?: unknown;
}

interface BackendSseMeaningShape {
  id?: string;
  pos?: string | null;
  senses?: BackendSseSenseShape[] | null;
}

interface BackendCardPayload {
  id?: string;
  // Бэкенд после snakeize отдаёт snake_case — но в payload-е JSON-объекте
  // мы храним то, что положил сервис (camelCase из CardSchema). На всякий
  // случай поддерживаем оба варианта.
  front_name?: string;
  frontName?: string;
  primary_translation?: string | null;
  primaryTranslation?: string | null;
  transcription?: string | null;
  cefr?: string | null;
  meanings?: BackendSseMeaningShape[] | null;
  is_need_error_action?: boolean;
  isNeedErrorAction?: boolean;
  error_info?: string | string[] | null;
  errorInfo?: string | string[] | null;
  expect_word?: string[] | null;
  expectWord?: string[] | null;
  status?: string;
}

function normalizeSseMeanings(
  meanings: BackendSseMeaningShape[] | null | undefined,
): SseMeaning[] {
  if (!Array.isArray(meanings)) return [];
  return meanings.map((m) => ({
    id: m.id ?? '',
    pos: m.pos ?? null,
    senses: (m.senses ?? []).map((s) => ({
      id: s.id ?? '',
      translations: (s.translations ?? []).map((t) => ({
        id: t.id ?? '',
        text: t.text,
        gender: t.gender ?? null,
      })),
      enSynonyms: s.enSynonyms ?? s.en_synonyms ?? [],
      exampleEn: s.exampleEn ?? s.example_en ?? null,
      simpleExampleEn: s.simpleExampleEn ?? s.simple_example_en ?? null,
      simpleExampleRu: s.simpleExampleRu ?? s.simple_example_ru ?? null,
      hint: s.hint ?? null,
      selectionOptions: s.selectionOptions ?? s.selection_options ?? null,
      isRare: Boolean(s.isRare ?? s.is_rare),
      contexts: mapSenseContexts(s.contexts),
    })),
  }));
}

function pickPrimaryFromSse(card: BackendCardPayload | null): string {
  if (!card) return '';
  const direct = card.primaryTranslation ?? card.primary_translation;
  if (direct && direct.trim()) return direct.trim();
  const meanings = card.meanings ?? null;
  const first = meanings?.[0]?.senses?.[0]?.translations?.[0]?.text;
  return first?.trim() ?? '';
}

interface CardEventInput {
  notificationId: string;
  eventType: NotificationEventType;
  category: NotificationCategory;
  moduleId: string | null;
  card: BackendCardPayload | null;
  createdAt: string;
  isRead: boolean;
  /** selectionFailed: какие строки не добавились и почему. */
  failedPicks?: FailedPick[];
  reason?: string | null;
}

function normalizeErrorString(value: string | string[] | null | undefined): string {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.join('').trim();
  return '';
}

function normalizeExpectList(value: string[] | null | undefined): string[] {
  if (!Array.isArray(value) || value.length === 0) return [];
  const looksCharSplit =
    value.length >= 3 &&
    value[0] === '{' &&
    value[value.length - 1] === '}' &&
    value.every((c) => typeof c === 'string' && c.length <= 1);
  if (looksCharSplit) {
    const inner = value.slice(1, -1).join('');
    return inner
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return value.filter((v) => typeof v === 'string' && v.trim().length > 0);
}

const KNOWN_EVENTS: NotificationEventType[] = [
  'unExpectedError',
  'expectedWord',
  'succeeded',
  'selectionFailed',
];

function isKnownEvent(name: string): name is NotificationEventType {
  return (KNOWN_EVENTS as string[]).includes(name);
}

function buildActionRequiredLink(moduleId: string | null): string | null {
  // Триаж/«разбор» удалён — ошибочные генерации теперь ведут на страницу модуля
  // (там видно проблемную карточку), а не на несуществующий /action-required.
  return moduleId ? `/units/${moduleId}` : null;
}

function buildModuleLink(moduleId: string | null): string | null {
  return moduleId ? `/units/${moduleId}` : null;
}

function pickCardField<T>(card: BackendCardPayload | null, snake: keyof BackendCardPayload, camel: keyof BackendCardPayload): T | null {
  if (!card) return null;
  const v = (card[snake] ?? card[camel]) as T | undefined | null;
  return v ?? null;
}

function renderCardCreationNotification(input: CardEventInput): NotificationDto | null {
  const { eventType, card, moduleId, notificationId, createdAt, isRead, category } = input;
  const word = pickCardField<string>(card, 'front_name', 'frontName');
  const ru = pickPrimaryFromSse(card);
  const tone = NOTIFIABLE_TONE[eventType] ?? 'info';

  let title = '';
  let body = '';
  let link: string | null = null;
  let actionLabel: string | null = null;

  if (eventType === 'unExpectedError') {
    const reason = normalizeErrorString(
      pickCardField<string | string[]>(card, 'error_info', 'errorInfo'),
    );
    title = word ? `Не удалось разобрать «${word}»` : 'Не удалось разобрать слово';
    body = reason
      ? reason
      : 'Алгоритм не справился со словом автоматически. Открой проблемные слова и реши, что с ним делать.';
    link = buildActionRequiredLink(moduleId);
    actionLabel = link ? 'Требуют действия' : null;
  } else if (eventType === 'expectedWord') {
    const suggestions = normalizeExpectList(
      pickCardField<string[]>(card, 'expect_word', 'expectWord'),
    );
    title = word ? `Опечатка в слове «${word}»?` : 'Возможная опечатка в слове';
    if (suggestions.length) {
      const list = suggestions.map((s) => `«${s}»`).join(', ');
      body = ru
        ? `Похоже, имелось в виду: ${list}. Перевод сейчас: ${ru}. Открой проблемные слова и выбери.`
        : `Похоже, имелось в виду: ${list}. Открой проблемные слова и выбери.`;
    } else {
      body = 'Алгоритм нашёл уточнение для слова — открой проблемные слова, чтобы посмотреть.';
    }
    link = buildActionRequiredLink(moduleId) ?? buildModuleLink(moduleId);
    actionLabel = link ? 'Требуют действия' : null;
  } else if (eventType === 'selectionFailed') {
    // Выбранная часть речи не стала карточкой: модель не дала её значение или
    // дописывание упало. Остальное (если было) уже добавлено, квота за пустую
    // генерацию возвращена.
    const rows = describeFailedPicks(input.failedPicks ?? []);
    title = word
      ? `Не добавили «${word}»${rows ? ` — ${rows}` : ''}`
      : 'Часть речи не добавилась';
    body =
      input.reason?.trim() ||
      'AI не собрал значение для выбранной части речи. Квота возвращена — попробуй ещё раз.';
    link = buildModuleLink(moduleId);
    actionLabel = link ? 'Открыть модуль' : null;
  } else {
    // succeeded — AI закончил генерацию; карточка уже готова к учёбе (сервер
    // авто-выбрал все части речи, шага подтверждения переводов больше нет).
    title = word
      ? `Карточка «${word}» готова`
      : 'Карточка готова';
    body = ru
      ? `Готово к учёбе — ${ru}. Можешь начинать повторять или открыть модуль.`
      : 'Карточка сгенерирована и готова к учёбе. Можешь начинать повторять.';
    link = moduleId ? `/units/${moduleId}/cards/new` : '/cards/new';
    actionLabel = 'Открыть';
  }

  return {
    id: notificationId,
    tone,
    title,
    body,
    createdAt,
    isRead,
    link,
    actionLabel,
    eventType,
    cardId: card?.id ?? null,
    front: word || null,
    moduleId,
    category,
  };
}

export function buildNotificationFromBackend(n: BackendNotification): NotificationDto | null {
  if (n.category !== 'card_creation') return null;
  if (!isKnownEvent(n.type)) return null;

  const payload = (n.payload ?? {}) as {
    module_id?: string;
    moduleId?: string;
    card?: BackendCardPayload | null;
    failed_picks?: unknown;
    failedPicks?: unknown;
    reason?: string | null;
  };
  return renderCardCreationNotification({
    notificationId: n.id,
    eventType: n.type,
    category: 'card_creation',
    moduleId: payload.module_id ?? payload.moduleId ?? null,
    card: payload.card ?? null,
    createdAt: n.created_at,
    isRead: n.is_read,
    failedPicks: readFailedPicks(payload.failed_picks ?? payload.failedPicks),
    reason: payload.reason ?? null,
  });
}

export interface SseCardEventMeta {
  eventType: NotificationEventType;
  moduleId: string | null;
  cardId: string | null;
  suggestions: string[];
  errorInfo: string | null;
  totalErrorCards: number | null;
  card: SseCardPayloadFull | null;
}

interface SseEventPayload {
  notificationId?: string;
  category?: string;
  eventType?: NotificationEventType;
  moduleId?: string;
  totalErrorCards?: number;
  total_error_cards?: number;
  card?: Partial<SseCardPayloadFull> | null;
  failedPicks?: unknown;
  reason?: string | null;
}

function readTotalErrorCards(payload: SseEventPayload): number | null {
  const raw = payload.totalErrorCards ?? payload.total_error_cards;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  return Math.max(0, Math.floor(raw));
}

export function parseCardEventMeta(
  eventName: string,
  rawPayload: unknown,
): SseCardEventMeta | null {
  if (!isKnownEvent(eventName)) return null;
  const payload = (rawPayload ?? {}) as SseEventPayload;
  const card = payload.card ?? null;

  return {
    eventType: eventName,
    moduleId: payload.moduleId ?? null,
    cardId: card?.id ?? null,
    suggestions: normalizeExpectList(card?.expectWord ?? null),
    errorInfo: normalizeErrorString(card?.errorInfo) || null,
    totalErrorCards: readTotalErrorCards(payload),
    card: card
      ? {
          id: card.id ?? '',
          frontName: card.frontName ?? '',
          primaryTranslation: card.primaryTranslation ?? null,
          transcription: card.transcription ?? null,
          cefr: card.cefr ?? null,
          meanings: normalizeSseMeanings(card.meanings as BackendSseMeaningShape[] | null | undefined),
          isNeedErrorAction: card.isNeedErrorAction ?? false,
          errorInfo: card.errorInfo ?? null,
          expectWord: card.expectWord ?? null,
          status: card.status ?? '',
        }
      : null,
  };
}

export function buildNotificationFromSse(
  eventName: string,
  rawPayload: unknown,
): NotificationDto | null {
  if (!isKnownEvent(eventName)) return null;
  const payload = (rawPayload ?? {}) as SseEventPayload;
  const card = payload.card ?? null;
  const id = payload.notificationId ?? `sse-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  return renderCardCreationNotification({
    notificationId: id,
    eventType: eventName,
    category: 'card_creation',
    moduleId: payload.moduleId ?? null,
    card: card as BackendCardPayload | null,
    createdAt: new Date().toISOString(),
    isRead: false,
    failedPicks: readFailedPicks(payload.failedPicks),
    reason: payload.reason ?? null,
  });
}
